import { v } from "convex/values";

/**
 * Organization roles, mirrored from WorkOS role slugs. `member` is the existing editor role and
 * keeps its slug so current WorkOS memberships and persisted users stay valid.
 */
export const ORG_ROLES = ["owner", "admin", "member", "reviewer"] as const;
export type OrgRole = (typeof ORG_ROLES)[number];

export const orgRoleValidator = v.union(
  v.literal("owner"),
  v.literal("admin"),
  v.literal("member"),
  v.literal("reviewer")
);

/**
 * What a role may do, independent of organization payment status. Commercial access
 * (`assertOrganizationWriteAccess`) is checked separately and never grants a capability.
 */
export type OrgCapability =
  // See every project in the organization; otherwise only projects with a projectAccess row.
  | "view_all_projects"
  | "view_assigned_projects"
  | "download_completed_exports"
  // Create, edit, upload, and start processing project work.
  | "edit_work"
  | "manage_members"
  | "manage_billing";

const ROLE_CAPABILITIES: Record<OrgRole, ReadonlySet<OrgCapability>> = {
  owner: new Set<OrgCapability>([
    "view_all_projects", "view_assigned_projects", "download_completed_exports",
    "edit_work", "manage_members", "manage_billing",
  ]),
  admin: new Set<OrgCapability>([
    "view_all_projects", "view_assigned_projects", "download_completed_exports",
    "edit_work", "manage_members", "manage_billing",
  ]),
  member: new Set<OrgCapability>(["view_assigned_projects", "download_completed_exports", "edit_work"]),
  reviewer: new Set<OrgCapability>(["view_assigned_projects", "download_completed_exports"]),
};

export function getOrgRoleCapabilities(role: OrgRole): ReadonlySet<OrgCapability> {
  return ROLE_CAPABILITIES[role];
}

export function orgRoleHasCapability(role: OrgRole, capability: OrgCapability): boolean {
  return ROLE_CAPABILITIES[role].has(capability);
}

/** Roles that only see projects they are explicitly assigned to. */
export function requiresProjectAssignment(role: OrgRole): boolean {
  return !orgRoleHasCapability(role, "view_all_projects");
}

export function isReviewerRole(role: OrgRole | null | undefined): role is "reviewer" {
  return role === "reviewer";
}

/**
 * Billable-seat classification: an active member who can create, process, or edit work occupies
 * a paid seat. Reviewers are free. Whether the organization actually pays (subscription, free
 * grant, internal trial) is an entitlement question answered elsewhere.
 */
export function isBillableSeatRole(role: OrgRole | null | undefined): boolean {
  return role !== null && role !== undefined && orgRoleHasCapability(role, "edit_work");
}
